/**
 * useImpronte — hybrid hook (cloud-first, LS fallback), ISOLATO PER UTENTE.
 *
 * Sprint isolamento multi-account:
 *   • Cache locale sempre SCOPED all'utente autenticato
 *     (`time-watch:impronte:user:<userId>`) o alla sessione anonima
 *     (`time-watch:impronte`).
 *   • Nessuna migrazione automatica di dati anonimi verso l'account appena
 *     autenticato: un nuovo account parte SEMPRE senza Impronte ereditate.
 *   • Al cambio utente (login/logout/switch), lo stato in memoria viene
 *     azzerato prima di caricare quello del nuovo utente.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_ANON = "time-watch:impronte";
const LS_USER_PREFIX = "time-watch:impronte:user:";

function lsKeyFor(userId) {
  return userId ? `${LS_USER_PREFIX}${userId}` : LS_ANON;
}

function currentPeriod() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
}

function isInPeriod(dateIso, period) {
  return typeof dateIso === "string" && dateIso.startsWith(period + "-");
}

function loadLocal(userId) {
  try {
    const raw = window.localStorage.getItem(lsKeyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocal(userId, arr) {
  try {
    window.localStorage.setItem(lsKeyFor(userId), JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

export function useImpronte() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = isAuthenticated && user && user.id ? user.id : null;
  // Parte SEMPRE vuoto per non mostrare dati stale al primo render dopo
  // il cambio utente.
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const currentUserRef = useRef(userId);

  // Reset immediato al cambio utente
  useEffect(() => {
    if (currentUserRef.current !== userId) {
      currentUserRef.current = userId;
      setItems(loadLocal(userId));
      setLoaded(false);
    }
  }, [userId]);

  const refresh = useCallback(async () => {
    const uidAtFetch = userId;
    if (!isAuthenticated) {
      setItems(loadLocal(null));
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/impronte`);
      // Discard stale response se l'utente è cambiato durante la fetch
      if (currentUserRef.current !== uidAtFetch) return;
      const cloud = Array.isArray(data.items) ? data.items : [];
      setItems(cloud);
      saveLocal(uidAtFetch, cloud);
    } catch {
      if (currentUserRef.current !== uidAtFetch) return;
      // Fallback: cache LS scoped-user (mai anonima)
      setItems(loadLocal(uidAtFetch));
    } finally {
      if (currentUserRef.current === uidAtFetch) setLoaded(true);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, isAuthenticated, userId, refresh]);

  const addImpronta = useCallback(
    async ({ date, category, note }) => {
      const cleanNote = typeof note === "string" ? note.trim() : "";
      if (isAuthenticated) {
        const { data } = await axios.post(`${API}/impronte`, {
          date,
          category,
          note: cleanNote,
        });
        setItems((prev) => {
          const next = [...prev, data];
          saveLocal(userId, next);
          return next;
        });
        return data;
      }
      // anonymous: append to LS with dedupe on date
      const existing = items.find((i) => i.date === date);
      if (existing) {
        const err = new Error("Hai già registrato la tua Impronta oggi");
        err.status = 409;
        throw err;
      }
      const entry = { date, category, note: cleanNote, at: new Date().toISOString() };
      setItems((prev) => {
        const next = [...prev, entry];
        saveLocal(null, next);
        return next;
      });
      return entry;
    },
    [isAuthenticated, items, userId]
  );

  // Filtra al mese corrente per la UI del TIME Watch: le Impronte storiche
  // restano in DB e vengono mostrate soltanto negli Archivi mensili.
  const period = currentPeriod();
  const itemsCurrentMonth = useMemo(
    () => items.filter((it) => isInPeriod(it.date, period)),
    [items, period]
  );

  // Aggregate counts + percentages (solo mese corrente)
  const stats = useMemo(() => {
    const counts = {};
    for (const it of itemsCurrentMonth) {
      counts[it.category] = (counts[it.category] || 0) + 1;
    }
    const total = itemsCurrentMonth.length;
    return { counts, total };
  }, [itemsCurrentMonth]);

  return {
    // `items` = mese corrente (fonte primaria per la UI TIME Watch e Area
    // Personale). `allItems` esposto per usi che devono vedere lo storico
    // completo (es. controllo "hai già registrato oggi").
    items: itemsCurrentMonth,
    allItems: items,
    loaded,
    total: stats.total,
    counts: stats.counts,
    addImpronta,
    refresh,
  };
}
